import { Dialog, DialogContent } from "../components/ui/dialog";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Heart, Minus, Plus, RotateCcw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import api, { formatApiError } from "../lib/api";
import { DropCountdown } from "./DropCountdown";

export const QuickView = ({ product, onClose }) => {
  const { add, setOpen } = useCart();
  const { ids, toggle } = useWishlist();
  const [size, setSize] = useState("M");
  const [wlEmail, setWlEmail] = useState("");
  const [wlDone, setWlDone] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);

  useEffect(() => {
    if (product) {
      setSize("M");
      setWlDone(false);
      setWlEmail("");
    }
  }, [product]);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setWlBusy(true);
    try {
      await api.post("/waitlist", { product: product.id, size, email: wlEmail });
      setWlDone(true);
      toast.success("You are on the list", { description: `We will write the moment size ${size} returns.` });
    } catch (err) {
      toast.error("Could not join the waitlist", { description: formatApiError(err) });
    } finally {
      setWlBusy(false);
    }
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[92vh] w-[94vw] max-w-4xl overflow-y-auto border border-navy/10 bg-cream p-0 sm:rounded-none"
        data-testid="quick-view-dialog"
      >
        {product && (
          <div className="grid md:grid-cols-2">
            <div className="relative h-72 overflow-hidden bg-white/5 md:h-auto md:min-h-[520px]">
              <TransformWrapper initialScale={1} minScale={1} maxScale={4} wheel={{ step: 0.12 }} doubleClick={{ mode: "toggle" }}>
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <>
                    <TransformComponent wrapperClass="!h-full !w-full" contentClass="!h-full !w-full">
                      <img src={product.image} alt={product.name} className="h-full w-full object-cover" data-testid="quick-view-image" />
                    </TransformComponent>
                    <div className="absolute bottom-4 left-4 flex gap-1.5">
                      <button onClick={() => zoomIn()} aria-label="Zoom in" data-testid="zoom-in-button" className="flex h-9 w-9 items-center justify-center bg-navy text-cream transition-colors duration-200 hover:bg-burgundy hover:text-white">
                        <Plus className="h-4 w-4" />
                      </button>
                      <button onClick={() => zoomOut()} aria-label="Zoom out" data-testid="zoom-out-button" className="flex h-9 w-9 items-center justify-center bg-navy text-cream transition-colors duration-200 hover:bg-burgundy hover:text-white">
                        <Minus className="h-4 w-4" />
                      </button>
                      <button onClick={() => resetTransform()} aria-label="Reset zoom" data-testid="zoom-reset-button" className="flex h-9 w-9 items-center justify-center bg-navy text-cream transition-colors duration-200 hover:bg-burgundy hover:text-white">
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="absolute right-4 top-4 bg-[#0B101E]/85 px-3 py-1.5 text-[0.58rem] font-bold uppercase tracking-[0.22em] text-navy/70 backdrop-blur">
                      Scroll or double-click to zoom
                    </span>
                  </>
                )}
              </TransformWrapper>
            </div>
            <div className="flex flex-col justify-center p-8 md:p-12">
              {product.tag && (
                <span className="mb-4 w-fit bg-burgundy px-3 py-1.5 text-[0.6rem] font-bold uppercase tracking-[0.26em] text-white">{product.tag}</span>
              )}
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.3em] text-gold">{product.category_name}</p>
              <h3 className="mt-3 font-serif text-4xl leading-tight text-navy" data-testid="quick-view-name">{product.name}</h3>
              <p className="mt-5 leading-relaxed text-navy/70" data-testid="quick-view-description">{product.description}</p>
              <div className="mt-6 flex items-center gap-6 text-[0.68rem] uppercase tracking-[0.2em] text-navy/55">
                <span>Combed cotton blend</span>
                <span className="h-3 w-px bg-navy/20" />
                <span>Hand-linked toe</span>
                <span className="h-3 w-px bg-navy/20" />
                <span>200-needle knit</span>
              </div>
              {product.drop_ends_at && <DropCountdown until={product.drop_ends_at} variant="block" className="mt-8" />}
              <div className="mt-8">
                <p className="mb-3 text-[0.62rem] font-bold uppercase tracking-[0.26em] text-navy/55">Select your size</p>
                <div className="flex max-w-xs gap-2" data-testid="quick-view-sizes">
                  {["S", "M", "L"].map((s) => {
                    const left = product.sizes?.[s] ?? 1;
                    const out = left <= 0;
                    return (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        data-testid={`quick-view-size-${s.toLowerCase()}`}
                        className={`flex-1 border py-3 text-[0.72rem] font-bold tracking-[0.2em] transition-colors duration-200 ${
                          out
                            ? size === s
                              ? "border-burgundy text-burgundy line-through"
                              : "border-navy/15 text-navy/35 line-through hover:border-burgundy hover:text-burgundy"
                            : size === s
                              ? "border-gold bg-gold text-[#0B101E]"
                              : "border-navy/20 text-navy/70 hover:border-gold hover:text-gold"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2.5 text-[0.62rem] uppercase tracking-[0.18em] text-navy/40">
                  S · EU 36–39 &nbsp; M · EU 39–42 &nbsp; L · EU 42–46
                  {(product.sizes?.[size] ?? 99) <= 5 && (product.sizes?.[size] ?? 0) > 0 && (
                    <span className="ml-3 text-burgundy" data-testid="quick-view-low-stock">Only {product.sizes[size]} left in {size}</span>
                  )}
                </p>
              </div>
              <div className="mt-7 border-t border-navy/10 pt-7">
                {(product.sizes?.[size] ?? 1) <= 0 ? (
                  <div data-testid="waitlist-block">
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-3xl text-gold" data-testid="quick-view-price">${product.price.toFixed(2)}</span>
                      <span className="bg-burgundy px-3 py-1.5 text-[0.6rem] font-bold uppercase tracking-[0.24em] text-white">Sold out in {size}</span>
                    </div>
                    {wlDone ? (
                      <p className="mt-5 border border-gold/40 bg-gold/10 px-5 py-4 font-serif text-lg italic text-navy" data-testid="waitlist-success">
                        Noted in gold ink. We will write the moment size {size} returns.
                      </p>
                    ) : (
                      <form onSubmit={joinWaitlist} className="mt-5 flex flex-col gap-2.5 sm:flex-row" data-testid="waitlist-form">
                        <input
                          type="email"
                          required
                          value={wlEmail}
                          onChange={(e) => setWlEmail(e.target.value)}
                          placeholder="your@finest-address.com"
                          data-testid="waitlist-email-input"
                          className="h-12 flex-1 border border-navy/20 bg-white/5 px-4 text-navy placeholder:text-navy/35 focus:border-gold focus:outline-none"
                        />
                        <button
                          type="submit"
                          disabled={wlBusy}
                          data-testid="waitlist-submit"
                          className="h-12 bg-gold px-6 text-[0.65rem] font-bold uppercase tracking-[0.24em] text-[#0B101E] transition-colors duration-300 hover:bg-navy hover:text-gold disabled:opacity-60"
                        >
                          {wlBusy ? "Noting…" : "Notify Me"}
                        </button>
                      </form>
                    )}
                    <p className="mt-3 text-[0.62rem] uppercase tracking-[0.2em] text-navy/40">First word when the vault reopens. No spam, ever.</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-3xl text-gold" data-testid="quick-view-price">${product.price.toFixed(2)}</span>
                    <button
                      onClick={() => {
                        add(product, size);
                        onClose();
                        setOpen(true);
                      }}
                      data-testid="quick-view-add-to-cart"
                      className="group relative overflow-hidden bg-navy px-8 py-4 text-[0.68rem] font-bold uppercase tracking-[0.26em] text-cream"
                    >
                      <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
                      <span className="relative transition-colors duration-500 group-hover:text-[#0B101E]">Add Elegance to Your Cart</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {product && (
          <button onClick={() => toggle(product)} aria-label="Save to wishlist" data-testid="quick-view-wishlist" className="absolute right-[4.25rem] top-4 z-10 flex h-9 w-9 items-center justify-center bg-navy text-cream transition-colors duration-200 hover:bg-burgundy">
            <Heart className={`h-4 w-4 ${ids.has(product.id) ? "fill-burgundy text-burgundy" : ""}`} />
          </button>
        )}
        <button onClick={onClose} aria-label="Close quick view" data-testid="quick-view-close" className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center bg-navy text-cream transition-colors duration-200 hover:bg-burgundy hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
};
